/**
 * <sticky-atc>
 *
 * Mobile sticky add-to-cart bar. The markup is rendered server-side and the
 * button is a real submit control for the main product form (form=… name=add),
 * so add-to-cart works with zero JS proxying and opens the cart drawer through
 * the theme's existing product-form handler.
 *
 * This element does two things only:
 *   1. Reveals the bar once the primary buy button scrolls out of view.
 *   2. Mirrors the live price and the sold-out / disabled state from the main
 *      product info whenever the variant changes, so the bar never shows a
 *      stale price. It reads from the same nodes Dawn re-renders via the
 *      Section Rendering API, so it stays correct without duplicating any
 *      variant logic.
 *
 * Visibility is governed by CSS (a max-width media query), so resizing between
 * mobile and desktop never leaves a half-initialised bar — the previous
 * implementation built itself once at load and broke on rotate/resize.
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.priceTarget = this.querySelector('[data-sticky-price]');
    this.submitButton = this.querySelector('[data-sticky-submit]');
    this.submitText = this.querySelector('[data-sticky-submit-text]');

    this.priceSource = document.querySelector(this.dataset.priceSource);
    this.submitSource = document.querySelector(this.dataset.submitSource);
    this.observeTarget = document.querySelector(this.dataset.observe);

    this.setupVisibility();
    this.setupVariantSync();

    // Reveal only after wiring, so it never flashes before it can be hidden.
    this.hidden = false;
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.submitObserver?.disconnect();
  }

  setupVisibility() {
    if (!this.observeTarget || !('IntersectionObserver' in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Show once the primary buy button has scrolled up out of view.
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
      },
      { threshold: 0 },
    );

    this.intersectionObserver.observe(this.observeTarget);
  }

  setupVariantSync() {
    if (!('MutationObserver' in window)) return;

    // Mirror the main price into the bar whenever Dawn re-renders it.
    if (this.priceSource && this.priceTarget) {
      this.syncPrice();
      this.priceObserver = new MutationObserver(() => this.syncPrice());
      this.priceObserver.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
    }

    // Mirror the sold-out / disabled state from the main submit button.
    if (this.submitSource && this.submitButton) {
      this.syncSubmit();
      this.submitObserver = new MutationObserver(() => this.syncSubmit());
      this.submitObserver.observe(this.submitSource, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
      });
    }
  }

  syncPrice() {
    const price = this.priceSource.querySelector('.price');
    if (price) this.priceTarget.innerHTML = price.outerHTML;
  }

  syncSubmit() {
    const disabled = this.submitSource.hasAttribute('disabled');
    this.submitButton.toggleAttribute('disabled', disabled);

    const sourceText = this.submitSource.querySelector('span:not(.sold-out-message)');
    if (sourceText && this.submitText) {
      const text = sourceText.textContent.trim();
      if (text) this.submitText.textContent = text;
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
